import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Link from '@atlaskit/link';
import { mcpInfoCardMessages } from './messages';

import McpLogo from '#assets/illustrations/mcp/mcp-logo.svg';

const cardStyle = xcss({
  backgroundColor: 'elevation.surface',
  borderRadius: 'radius.large',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
  padding: 'space.200',
});

const logoBoxStyle = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
  width: '28px',
  height: '28px',
  borderRadius: 'radius.medium',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
});

type McpInfoCardTwoStepProps = {
  title: MessageDescriptor;
  body: MessageDescriptor;
  learnMoreUrl: string;
};

/** Compact info card shown when the consent is split into two steps. */
const McpInfoCardTwoStep: FunctionComponent<McpInfoCardTwoStepProps> = ({
  title,
  body,
  learnMoreUrl,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Box
      xcss={cardStyle}
      role="region"
      aria-label={formatMessage(mcpInfoCardMessages.carouselRegionAriaLabel)}
      tabIndex={-1}
    >
      <Stack space="space.100" alignInline="start">
        <Inline space="space.100" alignBlock="center">
          <Box xcss={logoBoxStyle}>
            <Image src={McpLogo} alt="" />
          </Box>
          <Heading size="xsmall">
            <FormattedMessage {...title} />
          </Heading>
        </Inline>
        <Text as="p" color="color.text.subtle">
          <FormattedMessage {...body} />
        </Text>
        <Link href={learnMoreUrl} target="_blank" rel="noopener noreferrer">
          <FormattedMessage {...mcpInfoCardMessages.twoStepLearnMore} />
        </Link>
      </Stack>
    </Box>
  );
};

export default McpInfoCardTwoStep;
